import { readableColor } from 'polished';

/**
 * These exact strings are load-bearing. TicketCardv2 also shades its logo strip off the foreground,
 * and it does so by testing `foreground.toLowerCase()` against `['#ffffff', '#fff']` rather than by
 * reading the colour: a match darkens the strip, anything else lightens it. `#FFFFFF` matches;
 * `white` or `rgb(255,255,255)` would not, and would silently wash out every dark ticket.
 */
export const CONTRAST_DARK = '#000000';
export const CONTRAST_LIGHT = '#FFFFFF';

/**
 * Black or white, whichever reads better on the given background.
 *
 * The WCAG 2.1 relative luminance comparison lives in `polished`, which the design system itself
 * depends on - see https://polished.js.org/docs/#readablecolor. It splits at the 0.179 crossover where
 * pure black and pure white give the same contrast ratio, then confirms the result clears AA 4.5:1.
 *
 * ars-service pairs a VIP background with an unrelated `foregroundColor` fact and never checks the two
 * against each other, which renders white on `#FFFF78`. Deriving the foreground instead is a deliberate
 * divergence from that behaviour.
 *
 * `readableColor` throws rather than returning on a colour it cannot parse, so fall back to white -
 * the light-on-dark default the design system already uses for an uncoloured ticket.
 */
export function getContrastingFontColor(backgroundHex: string | null | undefined): string {
  if (!backgroundHex) {
    return CONTRAST_LIGHT;
  }

  try {
    return readableColor(backgroundHex.trim(), CONTRAST_DARK, CONTRAST_LIGHT);
  } catch {
    return CONTRAST_LIGHT;
  }
}
